import React, { FC, UIEvent, useEffect, useState, useRef } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import { Avatar } from '@src/components/Avatar';
import { getNameInitials } from '@src/util/getNameInitials';
import { relativeTime } from '@src/util/relativeTime';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import {
  useGetTaModalApplicantLazyQuery,
  useGetVerifiedUserQuery,
  UserVerificationStateEnum,
  useSessionQuery,
} from '@src/graphql/generated';
import { getAverageResponseTimeLabel } from '@src/util/formatTime';
import { Footer } from './Footer';
import { ConfirmMessage } from './ConfirmMessage';
import { HighlightBox } from './HighlightBox';
import { StageCard } from './StageCard';
import { FooterForModal } from './FooterForModal';
import { Skeleton } from './Skeleton';
import { DirectConnect } from './DirectConnect';
import { VirtualRecruiterOffers } from './VirtualRecruiterOffers';
import { ConnectParams } from './interfaces/connectParams.interface';
import { UserDetails } from './UserDetails';
import { ResponseTime } from './ResponseTime';
import { Timestamp } from './Timestamp';
import { WorkHistory } from './WorkHistory';
import { TalentCardProps } from './props';
import './styles.scss';

export const TalentCard: FC<TalentCardProps> = ({
  onConfirmTalent,
  onDiscardTalent,
  openingId,
  goBack,
  goNext,
  isInvited,
  isDirectConnectShownDefault,
  customPitchMessage,
  isVisible,
  openingState,
  isFooterVisible,
  isContactInfoVisible,
  wrapperClassName,
  isModalView,
  onClose,
  userId,
  userPartialData,
  isFeaturedApplicantModal,
  shouldFetchApplicant,
  isKeyboardDisable,
  areVerificationStagesHidden,
  setIsKeyboardDisable,
  isHideActionVisible,
}) => {
  const { data: sessionData } = useSessionQuery();

  const recruiter = sessionData?.currentRecruiter;
  // const planInfo = sessionData?.customer.planInfo;
  const recruiterCurrencyCode = recruiter?.country?.currency || DEFAULT_CURRENCY_CODE;

  // const isInactiveSubscription = false;
  // Allowing to use the feature for all users for now
  // const isInactiveSubscription = !!isSubscriptionPlanType(planInfo) && !!planInfo.state && !!INACTIVE_SUBSCRIPTION_STATES.includes(planInfo.state);

  const [fetchTaModalApplicant, { data: taModalApplicantData, loading: isTaModalApplicantLoading }] =
    useGetTaModalApplicantLazyQuery();

  const { data: verifiedUserData, loading: isVerifiedUserLoading } = useGetVerifiedUserQuery({
    variables: {
      recruiterCurrencyCode: recruiterCurrencyCode,
      userId: userId,
    },
  });

  const [isConfirmMessageActive, setIsConfirmMessageActive] = useState(false);

  const [isFakeLoading, setIsFakeLoading] = useState(true);

  const [isDirectConnectShown, setIsDirectConnectShown] = useState(!!isDirectConnectShownDefault);

  const [hasScrolled, setHasScrolled] = useState(false);

  const [isVirtualRecruiterOffersShown, setIsVirtualRecruiterOffersShown] = useState(false);

  const activeTalentConfirmParams = useRef<ConnectParams>({});

  const userData = verifiedUserData?.verifiedUser.user;
  const userApplicantData = taModalApplicantData?.applicant.user;
  const userVerification = verifiedUserData?.verifiedUser?.userVerification;
  const userVerificationPartial = userPartialData?.userVerification;

  const workExperience = userVerification?.workExperience;
  const userVerificationState = userVerification?.state;
  const achievements = userVerification?.achievements;
  const canStartIn = userVerification?.canStartIn;
  const publishedAt = userVerificationPartial?.publishedAt || userVerification?.publishedAt;
  const positionExpectations = userVerificationPartial?.positionExpectations || userVerification?.positionExpectations;
  const averageResponseTimeInMs = userData?.averageResponseTimeInMs || userApplicantData?.averageResponseTimeInMs;
  const links = userData?.profile.links || userApplicantData?.profile.links || [];
  const skills = userData?.profile.skills || userApplicantData?.profile.skills || [];
  const isRelocation = userData?.profile.isRelocation || userApplicantData?.profile.isRelocation;
  const userPosition = userPartialData?.user.position || userData?.position || userApplicantData?.position;
  const location =
    userPartialData?.user.profile.location.shortName ||
    userData?.profile.location.shortName ||
    userApplicantData?.profile.location.shortName;
  const userAvatarColor = userPartialData?.user.avatarColor || userData?.avatarColor || userApplicantData?.avatarColor;
  const workHistory =
    userData?.profile.workHistory ||
    userApplicantData?.profile.workHistory ||
    userPartialData?.user.profile.workHistory;
  const lastWorkPlaceCompanyName = workHistory?.[0]?.companyName;
  const convertedSalary =
    userPartialData?.user.profile.convertedSalary ||
    userData?.profile.convertedSalary ||
    userApplicantData?.profile.convertedSalary;
  const originalSalary =
    userPartialData?.user.profile.originalSalary ||
    userData?.profile.originalSalary ||
    userApplicantData?.profile.originalSalary;
  const isRemote =
    userPartialData?.user.profile.isRemote || userData?.profile.isRemote || userApplicantData?.profile.isRemote;
  const avatarImageUrl =
    userPartialData?.user.avatar?.fullUrl || userData?.avatar?.fullUrl || userApplicantData?.avatar?.fullUrl;
  const userName = userPartialData?.user.name || userData?.name || userApplicantData?.name;
  const userLastSeenAt = userPartialData?.user.lastSeenAt || userData?.lastSeenAt || userApplicantData?.lastSeenAt;
  const email = userData?.email || userApplicantData?.email;
  const countryCode = userData?.phone?.country?.dialCode || userApplicantData?.phone?.country?.dialCode;
  const phoneNumber = userData?.phone?.number || userApplicantData?.phone?.number;
  const phone = countryCode && phoneNumber ? `${countryCode} ${phoneNumber}` : undefined;

  const user = userData || userPartialData?.user || userApplicantData;

  const timeDifferenceInDays = moment().diff(userLastSeenAt, 'day');
  const wasUserActiveToday = timeDifferenceInDays === 0;
  const wasUserActiveThisWeek = timeDifferenceInDays > 0 && timeDifferenceInDays < 8;
  const wasUserActiveRecently = wasUserActiveToday || wasUserActiveThisWeek;
  const averageResponseTime = averageResponseTimeInMs
    ? getAverageResponseTimeLabel(averageResponseTimeInMs)
    : undefined;

  const getUserNameLabel = (): string => {
    if (userName && lastWorkPlaceCompanyName) {
      return `${userName} • ${lastWorkPlaceCompanyName}`;
    }

    return userName || 'Name Surname • Company';
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    const hasScrolled = event.currentTarget.scrollTop > 0;

    setHasScrolled(hasScrolled);
  };

  const handleConfirm = (connectParams: ConnectParams): void => {
    const payload: ConnectParams = {
      openingId: openingId,
      ...activeTalentConfirmParams.current,
      ...connectParams,
    };

    onConfirmTalent?.(payload);

    if (payload.openingId) {
      goNext?.();

      return;
    }

    setIsConfirmMessageActive(true);
  };

  const handleDirectConnect = (params?: ConnectParams): void => {
    // setState function should be after setting params inside useRef to pass pitch message into message popup input.
    activeTalentConfirmParams.current = params || {};

    if (!isKeyboardDisable) {
      setIsKeyboardDisable?.(true);
    }

    setIsDirectConnectShown(true);
  };

  useEffect(() => {
    if (isVisible && !isFakeLoading) {
      setIsFakeLoading(true);
    }

    if (isVisible && openingId && shouldFetchApplicant) {
      fetchTaModalApplicant({
        variables: {
          openingId: openingId,
          recruiterCurrencyCode: recruiterCurrencyCode,
          userId: userId,
        },
      });
    }
  }, [isVisible, shouldFetchApplicant]);

  useEffect(() => {
    if (isVisible && isFakeLoading) {
      setTimeout(() => {
        setIsFakeLoading(false);
      }, 500);
    }
  }, [isFakeLoading, isVisible]);

  if (isTaModalApplicantLoading || isVerifiedUserLoading) {
    return (
      <div className="ta-carousel-card">
        <Skeleton wrapperClassName="ta-carousel-card__loading" />
      </div>
    );
  }

  const isPublishedLessThanTwoMonthsAgo = moment().subtract(2, 'months').isBefore(publishedAt);

  return (
    <div className={classnames('ta-carousel-card', wrapperClassName)}>
      <div className="ta-carousel-card__body">
        <div className="ta-carousel-card__left-section-wrapper">
          <div className="ta-carousel-card__left-section">
            <div className="ta-carousel-card__avatar-wrapper">
              <div className="ta-carousel-card__avatar-container">
                <div className="ta-carousel-card__avatar-image">
                  <Avatar
                    avatarColor={userAvatarColor || undefined}
                    className="ta-carousel-card__avatar"
                    initials={getNameInitials(userName)}
                    source={avatarImageUrl}
                  />
                </div>

                {wasUserActiveRecently && <span className="ta-carousel-card__user-activity-status" />}
              </div>
            </div>

            <UserDetails
              links={links}
              location={location}
              userId={userId}
              userNameLabel={getUserNameLabel()}
              userPosition={userPosition || undefined}
            />

            {!areVerificationStagesHidden && userVerificationState && (
              <StageCard
                activeOpeningId={openingId}
                connectedWithCompaniesTotalCount={userVerification.connectedWithCompanies.pagination.totalCount}
                isConnectActionDisabled={isModalView}
                messagedByCompaniesTotalCount={userVerification.messagedByCompanies.pagination.totalCount}
                onConfirm={handleDirectConnect}
                userVerificationState={userVerification.state}
              />
            )}

            {averageResponseTime && <ResponseTime averageResponseTime={averageResponseTime} />}
          </div>
        </div>

        <div className="ta-carousel-card__right-section-wrapper">
          <div className="ta-carousel-card__right-section-top">
            {positionExpectations && (
              <div className="ta-carousel-card__right-highlight-section">
                <HighlightBox
                  isDense={hasScrolled}
                  isLoading={isFakeLoading}
                  text={positionExpectations}
                  timestamp={
                    isPublishedLessThanTwoMonthsAgo && publishedAt ? relativeTime(publishedAt, false) : undefined
                  }
                />
              </div>
            )}

            {workExperience && publishedAt && (
              <div
                className={classnames(
                  'ta-carousel-card__right-section-top-info',
                  !isFakeLoading && 'ta-carousel-card__right-section-top-info--visible',
                )}
              >
                {publishedAt && <Timestamp publishedAt={publishedAt} userName={userName || undefined} />}

                {workExperience && (
                  <h3
                    className={classnames(
                      'ta-carousel-card__wrapper-title',
                      hasScrolled && 'ta-carousel-card__wrapper-title--border-bottom',
                    )}
                  >
                    My work experience
                  </h3>
                )}
              </div>
            )}
          </div>

          <div
            className={classnames(
              'ta-carousel-card__right-section',
              !isFakeLoading && 'ta-carousel-card__right-section--visible',
            )}
            onScroll={handleScroll}
          >
            <WorkHistory
              achievements={achievements || undefined}
              canStartIn={canStartIn}
              convertedSalary={convertedSalary}
              isFakeLoading={isFakeLoading}
              isRelocation={!!isRelocation}
              isRemote={!!isRemote}
              isVisible={!!isVisible}
              location={location}
              originalSalary={originalSalary}
              skills={skills}
              workExperience={workExperience}
              workHistory={workHistory}
              email={isContactInfoVisible ? email || undefined : undefined}
              phone={isContactInfoVisible ? phone : undefined}
            />
          </div>
        </div>
      </div>

      {isFooterVisible &&
        (isModalView ? (
          <FooterForModal onClose={() => onClose?.()} user={user} />
        ) : (
          <Footer
            activeOpeningId={openingId}
            areActionsEnabled={isVisible && userVerificationState === UserVerificationStateEnum.PUBLISHED}
            goBack={goBack}
            goNext={goNext}
            isFeaturedApplicantModal={isFeaturedApplicantModal}
            isHideActionVisible={isHideActionVisible}
            isInvited={isInvited}
            onConfirm={handleDirectConnect}
            onDiscard={() => {
              if (onDiscardTalent) {
                onDiscardTalent(userId, openingId);
              }
            }}
            onVirtualRecruiterClick={() => setIsVirtualRecruiterOffersShown(true)}
            openingState={openingState}
            user={user}
          />
        ))}

      <DirectConnect
        isShown={isDirectConnectShown && !!isVisible}
        openingId={openingId}
        onClose={() => {
          setIsDirectConnectShown(false);

          setIsKeyboardDisable?.(false);
        }}
        onConfirm={connectParams => {
          handleConfirm?.({
            openingId: openingId,
            ...activeTalentConfirmParams.current,
            ...connectParams,
          });

          setIsDirectConnectShown(false);

          setIsKeyboardDisable?.(false);
        }}
        pitchMessage={customPitchMessage || activeTalentConfirmParams.current.pitch || ''}
      />

      {isVirtualRecruiterOffersShown && user?.id && (
        <VirtualRecruiterOffers
          onClose={() => setIsVirtualRecruiterOffersShown(false)}
          onOpeningSelect={connectParams => {
            activeTalentConfirmParams.current = connectParams;

            setIsVirtualRecruiterOffersShown(false);

            setIsDirectConnectShown(true);
          }}
          userId={user?.id}
        />
      )}

      <ConfirmMessage
        isActive={isConfirmMessageActive}
        onContinue={() => {
          setIsConfirmMessageActive(false);

          goNext?.();
        }}
      />
    </div>
  );
};
